import { useState } from "react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Check, Loader2, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface IOSUpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planName: string;
  planId: string;
  billingCycle: "monthly" | "yearly";
  userEmail: string;
}

export const IOSUpgradeModal = ({
  open,
  onOpenChange,
  planName,
  planId,
  billingCycle,
  userEmail,
}: IOSUpgradeModalProps) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSendEmail = async () => {
    setSending(true);

    try {
      // Generate checkout link and send via email
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          planId,
          billingCycle,
          successUrl: `${window.location.origin}/documents?success=true`,
          cancelUrl: `${window.location.origin}/pricing?canceled=true`,
          sendEmail: true, // Flag to send email instead of redirect
        },
      });

      if (error) throw error;

      setSent(true);
      toast.success("Email envoyé avec succès !");
    } catch (error: any) {
      console.error("Error sending upgrade email:", error);
      toast.error("Erreur lors de l'envoi de l'email. Veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after animation
    setTimeout(() => setSent(false), 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center mb-4">
            {sent ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
              >
                <Check className="w-8 h-8 text-primary" />
              </motion.div>
            ) : (
              <Sparkles className="w-8 h-8 text-primary" />
            )}
          </div>
          <DialogTitle className="text-xl">
            {sent ? "Email envoyé !" : `Passer à ${planName}`}
          </DialogTitle>
          <DialogDescription className="text-center pt-2">
            {sent ? (
              <>
                Un email contenant le lien de paiement sécurisé a été envoyé à{" "}
                <span className="font-medium text-foreground">{userEmail}</span>.
                <br />
                <span className="text-muted-foreground mt-2 block">
                  Consultez votre boîte de réception pour finaliser votre abonnement.
                </span>
              </>
            ) : (
              <>
                Pour des raisons de sécurité, les paiements ne peuvent pas être effectués directement depuis l'application.
                <br />
                <span className="text-muted-foreground mt-2 block">
                  Nous vous enverrons un email avec un lien sécurisé pour finaliser votre abonnement.
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {sent ? (
            <div className="space-y-3">
              <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Envoyé à :</span>
                  <span className="font-medium">{userEmail}</span>
                </div>
              </div>
              <Button onClick={handleClose} className="w-full" size="lg">
                Compris
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Email :</span>
                  <span className="font-medium">{userEmail}</span>
                </div>
              </div>

              <Button
                onClick={handleSendEmail}
                disabled={sending}
                className="w-full bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90"
                size="lg"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Recevoir le lien par email
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Vous recevrez un email dans les prochaines secondes.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper to detect iOS (with test override via ?simulateIOS=true)
export const isIOSDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  
  // Test mode: check URL param for simulation
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("simulateIOS") === "true") {
    return true;
  }
  
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isIOSWebView = /crios|fxios|opios|mercury/.test(userAgent) || 
                       (window.navigator as any).standalone === true;
  
  // Also check if running in Capacitor
  const isCapacitor = !!(window as any).Capacitor?.isNativePlatform?.();
  
  return isIOS || isIOSWebView || (isCapacitor && /ios/i.test((window as any).Capacitor?.getPlatform?.() || ""));
};
